import { PayloadAction } from '@reduxjs/toolkit'
import {
  AvatarVoiceTab,
  IAiAvatarState,
  IAvatarData,
  IAvatarGenerationParams,
  IAvatarGiftAccess,
  IClonedVoice,
  IPreviousSourceImage,
  IPublicAvatar,
  TVoiceCloneUploadStatus
} from 'store/types/aiAvatar'
import { IAvatarModels } from 'types/avatar'
import {
  avatarGenerationInitialParams,
  voiceCloneInitial
} from './initialState'

const reducers = {
  setIsLoadingTabData: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.isLoadingTabData = action.payload
  },
  setAvatar: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarData | null>
  ) => {
    state.generationParams.avatar = action.payload
    // Switching the source image abandons any in-flight Enhance/Resize: drop the
    // loader + correlation, otherwise a late `type:'ai-image'` result would land
    // on the NEW avatar (wrong url/ratio). The socket handler applies results to
    // whatever avatar is CURRENT, so the pending id must not outlive its avatar.
    // The undo snapshot dies with its avatar too — undoing after a switch would
    // resurrect an unrelated image.
    state.isAvatarLoading = false
    state.pendingSourceImageId = null
    state.pendingSourceAspectRatio = null
    state.previousSourceImage = null
  },
  setAvatarLoading: (state: IAiAvatarState, action: PayloadAction<boolean>) => {
    state.isAvatarLoading = action.payload
  },
  clearAvatar: (state: IAiAvatarState) => {
    state.generationParams.avatar = null
    // The aspect-ratio switcher lives only in the public-avatars panel, so
    // removing the avatar drops its override too — the next avatar (upload /
    // media library / another public one) starts from the project ratio.
    state.generationParams.aspectRatio = null
    state.isAvatarLoading = false
    // A delete during an in-flight Enhance/Resize abandons that op — drop the
    // correlation (and the undo snapshot) so a late socket result or an Undo
    // click can't resurrect the removed avatar.
    state.pendingSourceImageId = null
    state.pendingSourceAspectRatio = null
    state.previousSourceImage = null
  },
  // Marks a source-image Enhance/Resize as in flight: `id` is the ai-image
  // project-file id the `type:'ai-image'` socket will correlate on; `aspectRatio`
  // is committed to the avatar on completion (Resize's target ratio, or null for
  // Enhance which preserves the ratio). Set atomically so the socket handler
  // never sees a half-updated pair.
  setPendingSourceImage: (
    state: IAiAvatarState,
    action: PayloadAction<{ id: number; aspectRatio: string | null }>
  ) => {
    state.pendingSourceImageId = action.payload.id
    state.pendingSourceAspectRatio = action.payload.aspectRatio
  },
  clearPendingSourceImage: (state: IAiAvatarState) => {
    state.pendingSourceImageId = null
    state.pendingSourceAspectRatio = null
  },
  // Reseeds a FRESH form from the persisted case-1 storage entry after a page
  // reload: the kickoff-time avatar (+ its ratio override) comes back under the
  // loader, and the correlation is re-armed so the socket / reconcile fetch can
  // complete it. One atomic reducer, deliberately NOT `setAvatar` +
  // `setPendingSourceImage` — `setAvatar` treats an avatar change as "new
  // source" and would clear the very correlation being restored. The undo
  // snapshot stays empty: `applySourceImageResult` recreates it on completion
  // from the restored avatar, so post-reload Undo still works.
  restorePendingSourceImage: (
    state: IAiAvatarState,
    action: PayloadAction<{
      avatar: IAvatarData
      ratioOverride: string | null
      id: number
      aspectRatio: string | null
    }>
  ) => {
    const { avatar, ratioOverride, id, aspectRatio } = action.payload
    state.generationParams.avatar = avatar
    state.generationParams.aspectRatio = ratioOverride
    state.isAvatarLoading = true
    state.pendingSourceImageId = id
    state.pendingSourceAspectRatio = aspectRatio
    state.previousSourceImage = null
  },
  // Atomic success transition for a finished source-image Enhance/Resize (the
  // `type:'ai-image'` socket branch): snapshot the CURRENT avatar (+ ratio
  // override) into `previousSourceImage` for one-step undo, then apply the
  // generated image, commit the target ratio (Resize; null = keep, Enhance),
  // and release the loader + correlation. One reducer so the snapshot can never
  // diverge from the apply.
  applySourceImageResult: (
    state: IAiAvatarState,
    action: PayloadAction<{
      url: string
      width: number
      height: number
      aspectRatio: string | null
    }>
  ) => {
    const avatar = state.generationParams.avatar
    if (!avatar) return
    const { url, width, height, aspectRatio } = action.payload
    state.previousSourceImage = {
      avatar,
      aspectRatio: state.generationParams.aspectRatio
    }
    state.generationParams.avatar = { ...avatar, url, width, height }
    if (aspectRatio) {
      state.generationParams.aspectRatio = aspectRatio
    }
    state.isAvatarLoading = false
    state.pendingSourceImageId = null
    state.pendingSourceAspectRatio = null
  },
  // One-step undo of the last Enhance/Resize: restore the snapshotted avatar +
  // ratio and drop the snapshot (the Undo button hides itself). Deliberately
  // NOT routed through `setAvatar` — that reducer treats an avatar change as
  // "new source" and would clear this very snapshot mid-restore.
  undoSourceImageChange: (state: IAiAvatarState) => {
    if (!state.previousSourceImage) return
    state.generationParams.avatar = state.previousSourceImage.avatar
    state.generationParams.aspectRatio = state.previousSourceImage.aspectRatio
    state.previousSourceImage = null
  },
  // Arms the one-step Undo snapshot directly — used when a regen form loads
  // with a persisted Enhance/Resize result (case 2): the form shows the stored
  // result, and this snapshot of the backend originals makes the Undo button
  // survive reselect/reload. Must be dispatched AFTER `setGenerationParams`
  // (that reducer clears the snapshot).
  setPreviousSourceImage: (
    state: IAiAvatarState,
    action: PayloadAction<IPreviousSourceImage>
  ) => {
    state.previousSourceImage = action.payload
  },
  setVoiceLangCode: (state: IAiAvatarState, action: PayloadAction<string>) => {
    state.generationParams.voice.langCode = action.payload
  },
  setVoiceSpeakerId: (state: IAiAvatarState, action: PayloadAction<number>) => {
    state.generationParams.voice.speakerId = action.payload
  },
  setVoiceSpeed: (state: IAiAvatarState, action: PayloadAction<number>) => {
    state.generationParams.voice.speed = action.payload
  },
  setVoiceCloneTab: (
    state: IAiAvatarState,
    action: PayloadAction<AvatarVoiceTab>
  ) => {
    state.voiceClone.activeTab = action.payload
  },
  setClonedVoice: (
    state: IAiAvatarState,
    action: PayloadAction<IClonedVoice>
  ) => {
    state.voiceClone.voice = action.payload
    state.voiceClone.uploadStatus = 'idle'
  },
  setVoiceCloneUploadStatus: (
    state: IAiAvatarState,
    action: PayloadAction<TVoiceCloneUploadStatus>
  ) => {
    state.voiceClone.uploadStatus = action.payload
  },
  // Remembers the clone a generation was kicked off with, keyed by the
  // generation's `aiAvatarProjectId`, so the regen form / history prefill can
  // re-attach it after the `setGenerationParams` voice-clone reset (see
  // `restoreClonedVoice`). Never cleared within the session — entries are tiny
  // and a clip stays regenerable with its clone for the session's lifetime.
  rememberGenerationClone: (
    state: IAiAvatarState,
    action: PayloadAction<{ avatarProjectId: number; voice: IClonedVoice }>
  ) => {
    state.generationClones[action.payload.avatarProjectId] =
      action.payload.voice
  },
  // Detaches the cloned voice from the current avatar only — the uploaded file
  // itself stays in My Files.
  clearClonedVoice: (state: IAiAvatarState) => {
    state.voiceClone.voice = null
    state.voiceClone.uploadStatus = 'idle'
  },
  setQuality: (state: IAiAvatarState, action: PayloadAction<number>) => {
    state.generationParams.quality = action.payload
  },
  setSpeechText: (state: IAiAvatarState, action: PayloadAction<string>) => {
    state.generationParams.speechText = action.payload
  },
  setAvatarAspectRatio: (
    state: IAiAvatarState,
    action: PayloadAction<string | null>
  ) => {
    state.generationParams.aspectRatio = action.payload
  },
  setPublicAvatars: (
    state: IAiAvatarState,
    action: PayloadAction<IPublicAvatar[]>
  ) => {
    state.publicAvatars.data = action.payload
  },
  setPublicAvatarsLoading: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.publicAvatars.isLoading = action.payload
  },
  setAvatarModels: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarModels[]>
  ) => {
    state.avatarModels.data = action.payload
  },
  setAvatarModelsLoading: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.avatarModels.isLoading = action.payload
  },
  setGiftAccess: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarGiftAccess | null>
  ) => {
    state.giftAccess.data = action.payload
  },
  setGiftAccessLoading: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.giftAccess.isLoading = action.payload
  },
  setIsGenerating: (state: IAiAvatarState, action: PayloadAction<boolean>) => {
    state.isGenerating = action.payload
  },
  setAwaitingGiftRecheck: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.isAwaitingGiftRecheck = action.payload
  },
  // Replacing or resetting the form must also drop the voice-clone state: it
  // lives outside `generationParams`, and a stale clone left attached with the
  // Clone sub-tab active would silently override the loaded/reset `speakerId`
  // via `activeRefAudio` on the next trial preview or Generate. (The form-load
  // thunks then re-attach a clone-generated project's own clone on top of this
  // reset — see `restoreClonedVoice`.)
  setGenerationParams: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarGenerationParams>
  ) => {
    state.generationParams = action.payload
    state.voiceClone = voiceCloneInitial
    // Replacing the whole form (regen load / history prefill) swaps the avatar,
    // so abandon any in-flight source-image op AND the undo snapshot on it
    // (see `setAvatar`).
    state.isAvatarLoading = false
    state.pendingSourceImageId = null
    state.pendingSourceAspectRatio = null
    state.previousSourceImage = null
  },
  resetGenerationParams: (state: IAiAvatarState) => {
    state.generationParams = avatarGenerationInitialParams
    state.voiceClone = voiceCloneInitial
    state.isAvatarLoading = false
    state.pendingSourceImageId = null
    state.pendingSourceAspectRatio = null
    state.previousSourceImage = null
  }
}

export default reducers
